/**
 * The rich-text document a message, note or reply is written as.
 *
 * This is TipTap's JSON shape, narrowed to the vocabulary the portal allows:
 * paragraphs, bullet and numbered lists, code blocks, and inline text with
 * bold / italic / underline / strikethrough / code / link marks, plus mentions
 * of people. Nothing else — no headings, images, tables or raw HTML — so a
 * document can be rendered with plain React elements and never with
 * `innerHTML`. The API validates the same vocabulary before storing it
 * (`apps/api … /portal/domain/rich-text.ts`); keep the two in step.
 */

export interface RichTextLinkMark {
  readonly type: 'link';
  readonly attrs: { readonly href: string };
}

export type RichTextMark =
  | { readonly type: 'bold' }
  | { readonly type: 'italic' }
  | { readonly type: 'underline' }
  | { readonly type: 'strike' }
  | { readonly type: 'code' }
  | RichTextLinkMark;

export interface RichTextText {
  readonly type: 'text';
  readonly text: string;
  readonly marks?: readonly RichTextMark[];
}

export interface RichTextHardBreak {
  readonly type: 'hardBreak';
}

/** Someone named in the text. The id is a membership id. */
export interface RichTextMention {
  readonly type: 'mention';
  readonly attrs: { readonly id: string; readonly label: string };
}

export type RichTextInline = RichTextText | RichTextHardBreak | RichTextMention;

export interface RichTextParagraph {
  readonly type: 'paragraph';
  readonly content?: readonly RichTextInline[];
}

export interface RichTextListItem {
  readonly type: 'listItem';
  readonly content?: readonly (RichTextParagraph | RichTextBulletList | RichTextOrderedList)[];
}

export interface RichTextBulletList {
  readonly type: 'bulletList';
  readonly content?: readonly RichTextListItem[];
}

export interface RichTextOrderedList {
  readonly type: 'orderedList';
  readonly attrs?: { readonly start?: number };
  readonly content?: readonly RichTextListItem[];
}

export interface RichTextCodeBlock {
  readonly type: 'codeBlock';
  readonly content?: readonly RichTextText[];
}

/** A section heading in a Resource center article (the page title is the h1). */
export interface RichTextHeading {
  readonly type: 'heading';
  readonly attrs: { readonly level: 2 | 3 };
  readonly content?: readonly RichTextInline[];
}

/** A quoted passage in a Resource center article. */
export interface RichTextBlockquote {
  readonly type: 'blockquote';
  readonly content?: readonly (RichTextParagraph | RichTextBulletList | RichTextOrderedList)[];
}

/**
 * Headings and quotes only ever come from articles: the API refuses them in
 * messages, and accepts them in a Resource center article.
 */
export type RichTextBlock =
  | RichTextParagraph
  | RichTextBulletList
  | RichTextOrderedList
  | RichTextCodeBlock
  | RichTextHeading
  | RichTextBlockquote;

export interface RichTextDoc {
  readonly type: 'doc';
  readonly content?: readonly RichTextBlock[];
}

/** Link schemes a document may point at. Anything else is not a link. */
const LINK_SCHEMES = /^(https?:|mailto:)/i;

/**
 * Whether a link's target is one the portal will render as a link.
 *
 * @param href - The target.
 * @returns True for http(s) and mailto.
 */
export function isSafeHref(href: string): boolean {
  return LINK_SCHEMES.test(href.trim());
}

function inlineText(node: RichTextInline): string {
  switch (node.type) {
    case 'text':
      return node.text;
    case 'hardBreak':
      return '\n';
    case 'mention':
      return `@${node.attrs.label}`;
    default: {
      const never: never = node;
      return never;
    }
  }
}

function listItemText(item: RichTextListItem, prefix: string): string {
  return (item.content ?? [])
    .map((child, index) =>
      child.type === 'paragraph'
        ? `${index === 0 ? prefix : '  '}${(child.content ?? []).map(inlineText).join('')}`
        : blockText(child).replace(/^/gm, '  '),
    )
    .join('\n');
}

function blockText(block: RichTextBlock): string {
  switch (block.type) {
    case 'paragraph':
      return (block.content ?? []).map(inlineText).join('');
    case 'bulletList':
      return (block.content ?? []).map((item) => listItemText(item, '• ')).join('\n');
    case 'orderedList': {
      const start = block.attrs?.start ?? 1;
      return (block.content ?? [])
        .map((item, index) => listItemText(item, `${start + index}. `))
        .join('\n');
    }
    case 'codeBlock':
      return (block.content ?? []).map((node) => node.text).join('');
    case 'heading':
      return (block.content ?? []).map(inlineText).join('');
    case 'blockquote':
      return (block.content ?? []).map(blockText).join('\n');
    default: {
      const never: never = block;
      return never;
    }
  }
}

/**
 * The document as plain text: one line per paragraph, list items bulleted or
 * numbered, mentions as `@Name`. What a notification, a preview or a search
 * index reads.
 *
 * @param doc - The document.
 * @returns Its text, trimmed.
 */
export function plainText(doc: RichTextDoc): string {
  return (doc.content ?? []).map(blockText).join('\n').trim();
}

/**
 * Everyone mentioned, each once, in order of first mention.
 *
 * @param doc - The document.
 * @returns Membership ids.
 */
export function mentionedIds(doc: RichTextDoc): readonly string[] {
  const ids: string[] = [];
  const visit = (block: RichTextBlock | RichTextListItem): void => {
    for (const child of block.content ?? []) {
      if (child.type === 'mention') {
        if (!ids.includes(child.attrs.id)) ids.push(child.attrs.id);
      } else if ('content' in child) {
        visit(child);
      }
    }
  };
  (doc.content ?? []).forEach(visit);
  return ids;
}

/**
 * A document with nothing to say: no text, no mention.
 *
 * @param doc - The document.
 * @returns True when sending it would send nothing.
 */
export function isBlank(doc: RichTextDoc): boolean {
  return plainText(doc).length === 0 && mentionedIds(doc).length === 0;
}

/**
 * A one-paragraph document from plain text — for messages the system writes,
 * such as a shipment report.
 *
 * @param text - The text; blank lines split paragraphs.
 * @returns The document.
 */
export function docFromText(text: string): RichTextDoc {
  return {
    type: 'doc',
    content: text
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter((paragraph) => paragraph.length > 0)
      .map((paragraph) => ({
        type: 'paragraph',
        content: paragraph.split('\n').flatMap((line, index): RichTextInline[] =>
          index === 0 ? [{ type: 'text', text: line }] : [{ type: 'hardBreak' }, { type: 'text', text: line }],
        ),
      })),
  };
}

/* ─────────────────────── From the article editor ─────────────────────── */

/**
 * A node as TipTap's `getJSON()` hands it over: structurally typed, with
 * attributes the editor may have added. {@link articleDocFromEditor} narrows
 * it to the vocabulary, so nothing outside it ever reaches the API.
 */
export interface EditorJsonNode {
  readonly type?: string | undefined;
  readonly attrs?: Readonly<Record<string, unknown>> | undefined;
  readonly content?: readonly EditorJsonNode[] | undefined;
  readonly marks?: readonly { readonly type: string; readonly attrs?: Readonly<Record<string, unknown>> | undefined }[] | undefined;
  readonly text?: string | undefined;
}

const SIMPLE_MARKS = ['bold', 'italic', 'underline', 'strike', 'code'] as const;
type SimpleMark = (typeof SIMPLE_MARKS)[number];
const isSimpleMark = (type: string): type is SimpleMark => (SIMPLE_MARKS as readonly string[]).includes(type);

function marksFrom(node: EditorJsonNode): RichTextMark[] {
  const marks: RichTextMark[] = [];
  for (const mark of node.marks ?? []) {
    if (isSimpleMark(mark.type)) marks.push({ type: mark.type });
    const href = mark.attrs?.['href'];
    if (mark.type === 'link' && typeof href === 'string' && isSafeHref(href)) marks.push({ type: 'link', attrs: { href } });
  }
  return marks;
}

function inlineFrom(nodes: readonly EditorJsonNode[] | undefined): RichTextInline[] {
  const out: RichTextInline[] = [];
  for (const node of nodes ?? []) {
    if (node.type === 'hardBreak') out.push({ type: 'hardBreak' });
    if (node.type === 'text' && typeof node.text === 'string' && node.text !== '') {
      const marks = marksFrom(node);
      out.push(marks.length > 0 ? { type: 'text', text: node.text, marks } : { type: 'text', text: node.text });
    }
  }
  return out;
}

type ListChild = RichTextParagraph | RichTextBulletList | RichTextOrderedList;

function listChildrenFrom(nodes: readonly EditorJsonNode[] | undefined): ListChild[] {
  return blocksFrom(nodes).filter(
    (block): block is ListChild => block.type === 'paragraph' || block.type === 'bulletList' || block.type === 'orderedList',
  );
}

function listItemsFrom(nodes: readonly EditorJsonNode[] | undefined): RichTextListItem[] {
  return (nodes ?? [])
    .filter((node) => node.type === 'listItem')
    .map((node) => ({ type: 'listItem', content: listChildrenFrom(node.content) }));
}

function headingLevel(attrs: EditorJsonNode['attrs']): 2 | 3 {
  const level = attrs?.['level'];
  return typeof level === 'number' && level >= 3 ? 3 : 2;
}

function blocksFrom(nodes: readonly EditorJsonNode[] | undefined): RichTextBlock[] {
  const out: RichTextBlock[] = [];
  for (const node of nodes ?? []) {
    switch (node.type) {
      case 'paragraph':
        out.push({ type: 'paragraph', content: inlineFrom(node.content) });
        break;
      case 'heading':
        out.push({ type: 'heading', attrs: { level: headingLevel(node.attrs) }, content: inlineFrom(node.content) });
        break;
      case 'bulletList':
        out.push({ type: 'bulletList', content: listItemsFrom(node.content) });
        break;
      case 'orderedList': {
        const start = node.attrs?.['start'];
        out.push({
          type: 'orderedList',
          ...(typeof start === 'number' && start > 1 ? { attrs: { start } } : {}),
          content: listItemsFrom(node.content),
        });
        break;
      }
      case 'blockquote':
        out.push({ type: 'blockquote', content: listChildrenFrom(node.content) });
        break;
      case 'codeBlock': {
        const text = (node.content ?? []).map((child) => child.text ?? '').join('');
        out.push(text === '' ? { type: 'codeBlock' } : { type: 'codeBlock', content: [{ type: 'text', text }] });
        break;
      }
      default:
        // Anything outside the vocabulary (an image pasted in, a rule) is dropped.
        break;
    }
  }
  return out;
}

/**
 * An article as the editor holds it, narrowed to the portal vocabulary:
 * headings become h2 or h3, links keep only safe addresses, and anything the
 * vocabulary has no word for is left out. What comes back is what the API
 * stores and the portal renders with React elements.
 *
 * @param json - The editor's `getJSON()`.
 * @returns The article.
 */
export function articleDocFromEditor(json: EditorJsonNode): RichTextDoc {
  return { type: 'doc', content: blocksFrom(json.content) };
}
